// The interactive circle-of-fifths wheel for /circle-of-fifths: an SVG donut
// with majors on the outer ring, relative minors on the inner ring, and
// sharp/flat counts around the rim. Presentational and fully controlled —
// the parent owns the selected key and any quiz feedback flash.
//
// Props: {
//   selected: major key name | null,
//   onSelect(majorName, ring) — ring is "major" | "minor", whichever wedge
//     was clicked,
//   perspective?: "major" | "minor" — which ring reads as primary in the
//     selection highlight and center-hub label,
//   flash?: { major, ok } | null — quiz feedback tint on one wedge,
//   showDetails?: bool — minors + accidental counts (hidden during quizzes
//     so the wheel doesn't answer its own questions),
//   degreeBadges?: { [majorName]: { outer?, inner? } } — small roman-numeral
//     labels marking the selected key's diatonic chords on their wedges
//     (see circleOfFifths.wheelDegreeBadges),
//   arrow?: { from: majorKeyName, to: majorKeyName } | null — draws a curved
//     "resolves to" arrow between two wedges (e.g. V7 → I); only rendered
//     when showDetails is true and both keys are found in WHEEL,
// }

import React from "react";
import { WHEEL, signatureLabel, wheelEntry } from "../../lib/circleOfFifths";

const OUTER_R = 145;
const SPLIT_R = 95;
const INNER_R = 52;
const MAJOR_TEXT_R = 119;
const MINOR_TEXT_R = 72;
const COUNT_TEXT_R = 158;
// "Resolves to" arrow: endpoints sit between the hub and the minor ring, and
// the control point pulls in toward the hub so the curve bows inward rather
// than cutting straight across wedges.
const ARROW_R = 105;
const ARROW_CONTROL_R = 55;
// Degree badges hang directly below their key label in screen space (a fixed
// y offset, not a radial one) — radial placement collides with wide labels
// like "F#m" on the side wedges.
const OUTER_BADGE_DY = 17;
const INNER_BADGE_DY = 14;
const WEDGE_DEG = 30;

const polar = (r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180; // 0° at 12 o'clock
  return [r * Math.cos(rad), r * Math.sin(rad)];
};

// Shortest signed angular delta from `a` to `b`, in (-180, 180] — used so the
// arrow's bow always takes the short way around the wheel between wedges.
const angleDelta = (a, b) => (((b - a + 180) % 360) + 360) % 360 - 180;

// Annular-sector path between r0 and r1 across [deg0, deg1].
const wedgePath = (r0, r1, deg0, deg1) => {
  const [x0, y0] = polar(r1, deg0);
  const [x1, y1] = polar(r1, deg1);
  const [x2, y2] = polar(r0, deg1);
  const [x3, y3] = polar(r0, deg0);
  return [
    `M ${x0} ${y0}`,
    `A ${r1} ${r1} 0 0 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${r0} ${r0} 0 0 0 ${x3} ${y3}`,
    "Z",
  ].join(" ");
};

const wedgeFill = (major, ring, selected, flash, perspective) => {
  if (flash && flash.major === major) return flash.ok ? "#a7f3d0" : "#fecdd3"; // emerald/rose-200
  if (selected === major) {
    // Whichever ring matches the current perspective reads as primary.
    return ring === perspective ? "#c7d2fe" : "#e0e7ff"; // indigo-200/100
  }
  return ring === "major" ? "#ffffff" : "#f1f5f9"; // white / slate-100
};

const countBadge = (entry) => {
  if (entry.sharps) return `${entry.sharps}♯`;
  if (entry.flats) return `${entry.flats}♭`;
  return "·";
};

export default function Wheel({
  selected,
  onSelect,
  perspective = "major",
  flash = null,
  showDetails = true,
  degreeBadges = null,
  arrow = null,
}) {
  const selectedEntry = selected ? wheelEntry(selected) : null;

  let arrowGeometry = null;
  if (showDetails && arrow) {
    const fromIdx = WHEEL.findIndex((w) => w.major === arrow.from);
    const toIdx = WHEEL.findIndex((w) => w.major === arrow.to);
    if (fromIdx !== -1 && toIdx !== -1) {
      const fromMid = fromIdx * WEDGE_DEG;
      const toMid = toIdx * WEDGE_DEG;
      const midAngle = fromMid + angleDelta(fromMid, toMid) / 2;
      const [sx, sy] = polar(ARROW_R, fromMid);
      const [ex, ey] = polar(ARROW_R, toMid);
      const [ctrlX, ctrlY] = polar(ARROW_CONTROL_R, midAngle);
      arrowGeometry = { sx, sy, ctrlX, ctrlY, ex, ey };
    }
  }

  return (
    <svg
      viewBox="-172 -172 344 344"
      role="group"
      aria-label="circle of fifths"
      className="mx-auto w-full max-w-md select-none"
    >
      {WHEEL.map((entry, i) => {
        const mid = i * WEDGE_DEG;
        const deg0 = mid - WEDGE_DEG / 2;
        const deg1 = mid + WEDGE_DEG / 2;
        const [mx, my] = polar(MAJOR_TEXT_R, mid);
        const [nx, ny] = polar(MINOR_TEXT_R, mid);
        const [cx, cy] = polar(COUNT_TEXT_R, mid);
        const badge = (showDetails && degreeBadges && degreeBadges[entry.major]) || null;
        const [obx, oby] = [mx, my + OUTER_BADGE_DY];
        const [ibx, iby] = [nx, ny + INNER_BADGE_DY];
        return (
          <g key={entry.major}>
            {["major", "minor"].map((ring) => (
              <path
                key={ring}
                d={
                  ring === "major"
                    ? wedgePath(SPLIT_R, OUTER_R, deg0, deg1)
                    : wedgePath(INNER_R, SPLIT_R, deg0, deg1)
                }
                fill={wedgeFill(entry.major, ring, selected, flash, perspective)}
                stroke="#cbd5e1"
                strokeWidth="1"
                className="cursor-pointer transition-colors"
                role="button"
                aria-label={
                  ring === "major" ? `${entry.major} major` : `${entry.minor.slice(0, -1)} minor`
                }
                onClick={() => onSelect && onSelect(entry.major, ring)}
              />
            ))}
            <text
              x={mx}
              y={my}
              textAnchor="middle"
              dominantBaseline="central"
              className={
                entry.enharmonicMajor
                  ? "pointer-events-none fill-slate-800 text-[13px] font-semibold"
                  : "pointer-events-none fill-slate-800 text-[20px] font-semibold"
              }
            >
              {entry.enharmonicMajor ? `${entry.enharmonicMajor}/${entry.major}` : entry.major}
            </text>
            {badge && badge.outer && (
              <text
                x={obx}
                y={oby}
                textAnchor="middle"
                dominantBaseline="central"
                className="pointer-events-none fill-indigo-500 text-[11px] font-semibold"
              >
                {badge.outer}
              </text>
            )}
            {badge && badge.inner && (
              <text
                x={ibx}
                y={iby}
                textAnchor="middle"
                dominantBaseline="central"
                className="pointer-events-none fill-indigo-500 text-[10px] font-semibold"
              >
                {badge.inner}
              </text>
            )}
            {showDetails && (
              <>
                <text
                  x={nx}
                  y={ny}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className={
                    entry.enharmonicMinor
                      ? "pointer-events-none fill-slate-500 text-[9px]"
                      : "pointer-events-none fill-slate-500 text-[13px]"
                  }
                >
                  {entry.enharmonicMinor ? `${entry.enharmonicMinor}/${entry.minor}` : entry.minor}
                </text>
                <text
                  x={cx}
                  y={cy}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="pointer-events-none fill-slate-400 text-[11px]"
                >
                  {countBadge(entry)}
                </text>
              </>
            )}
          </g>
        );
      })}
      {arrowGeometry && (
        <g className="pointer-events-none" aria-hidden>
          <defs>
            <marker
              id="resolves-to-arrowhead"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 Z" fill="#f59e0b" />
            </marker>
          </defs>
          <path
            d={`M ${arrowGeometry.sx} ${arrowGeometry.sy} Q ${arrowGeometry.ctrlX} ${arrowGeometry.ctrlY} ${arrowGeometry.ex} ${arrowGeometry.ey}`}
            fill="none"
            stroke="#f59e0b"
            strokeWidth="2.5"
            strokeLinecap="round"
            markerEnd="url(#resolves-to-arrowhead)"
          />
        </g>
      )}
      <circle r={INNER_R - 4} fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
      {selectedEntry ? (
        <>
          <text
            textAnchor="middle"
            dominantBaseline="central"
            y={-8}
            className="pointer-events-none fill-slate-900 text-[22px] font-bold"
          >
            {perspective === "minor" ? selectedEntry.minor : selectedEntry.major}
          </text>
          <text
            textAnchor="middle"
            dominantBaseline="central"
            y={16}
            className="pointer-events-none fill-slate-500 text-[11px]"
          >
            {signatureLabel(selectedEntry)}
          </text>
        </>
      ) : (
        <text
          textAnchor="middle"
          dominantBaseline="central"
          className="pointer-events-none fill-slate-400 text-[12px]"
        >
          pick a key
        </text>
      )}
    </svg>
  );
}
